/* eslint-disable max-lines-per-function */
import { Link } from "react-router-dom";
import React from "react";
import { QueryResult, useMutation } from "@apollo/client";
import MaterialTable from "@material-table/core";
import { Grid } from "@mui/material";
import { actionFooterStyles, Button } from "components/haven-design";
import { ManuallyContacted,
  ProcessRemovalsView,
  ProcessRemovalsViewVariables,
  ProcessRemovalsView_interestList_scheduledUpdate_removals_results as RemovalsResult } from "types/gql-types";
import { NameColumnRaw, PhoneColumn, ReasonColumn } from "pages/waitlist-update/dashboard/components/custom-columns";
import { useSnackbar } from "components/haven-design/snackbar";
import { MANUALLY_CONTACTED } from "../query";
import { downloadRemovalsCsv } from "../download-csv";
import styles from "./step-removals.module.css";

type Props = {
  results: QueryResult<ProcessRemovalsView, ProcessRemovalsViewVariables>;
}

const KeepOnList: React.FC<{interestId:string, name:string | null, refetch:() => void}> =
({ interestId, name, refetch }) => {
  const { openSnackbar } = useSnackbar();
  const [submit] = useMutation<ManuallyContacted, { interest: string }>(MANUALLY_CONTACTED, {
    variables: { interest: interestId },
  });
  return (
    <Button
      size="S"
      color="secondary"
      className={styles.manuallyContacted}
      onClick={(event) => {
        event.preventDefault();
        submit().then((result) => {
          if (!result || !result.data) return;
          openSnackbar({ message: `${name ?? "The applicant"} has been kept on the list.`, status: "success" });
          refetch();
        }).catch(() => openSnackbar({ message: `Something went wrong when trying to keep ${name} on the list. Please try again.`, status: "error" }));
      }}
    >
      Keep on list
    </Button>);
};

const DEFAULT_PAGE_SIZE = 50;

const tableOptions = {
  sorting: false,
  showTitle: true,
  pageSize: DEFAULT_PAGE_SIZE,
  pageSizeOptions: [10, 20, 50, 100, 200, 500],
  paginationType: "stepped" as const,
  search: true,
  toolbar: true,
  padding: "dense" as const,
};

const Content: React.FC<Props> = (props) => {
  const { results: { fetchMore, data } } = props;
  const removals = data?.interestList.scheduledUpdate.removals;
  const tableRef = React.useRef();
  const [searchQuery, setSearchQuery] = React.useState("");

  // has removals should only apply when search is empty
  const hasRemovals = searchQuery === "" ? (removals?.totalCount ?? 0) > 0 : true;

  // @ts-ignore
  const refreshTable = () => tableRef.current?.onQueryChange();

  if (!hasRemovals) {
    return (
      <div>
        <h4>There are no applicants to remove.</h4>
        <div>
          <Link to={`/dash/manager/update/${data?.interestList.id ?? ""}`}>
            Navigate back to the waitlist update dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <h2>Review applicants who will be removed from the list ({removals?.totalCount})</h2>
        <Button
          size="S"
          color="primary"
          aria-label="Download removals CSV file"
          onClick={() => downloadRemovalsCsv(data?.interestList.id ?? "")}
        >
          Download CSV
        </Button>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <MaterialTable<RemovalsResult>
          title="Review applicants"
          tableRef={tableRef}
          components={{ Container: Grid }}
          columns={[
            { title: "Name", field: "name", render: (rowData:RemovalsResult) => <NameColumnRaw {...rowData} /> },
            { title: "Phone", field: "phone", render: (rowData:RemovalsResult) => <PhoneColumn value={rowData.phone ?? ""} /> },
            { title: "Status", field: "name", render: (rowData:RemovalsResult) => <ReasonColumn {...rowData} /> },
            { title: "Contact",
              field: "id",
              render: (rowData) => <KeepOnList {...rowData} refetch={refreshTable} /> },
          ]}
          data={(query) => {
            const limit = query.pageSize;
            const offset = query.page * limit;
            const { search } = query;
            setSearchQuery(search);
            return fetchMore({
              variables: {
                search,
                offset,
                limit,
              },
              updateQuery: (prev, { fetchMoreResult }) => {
                if (!fetchMoreResult) return prev;
                return fetchMoreResult;
              },
            }).then((res) => {
              const {
                results: newRows, totalCount: newTotalCount,
              } = res.data.interestList.scheduledUpdate.removals;

              return {
                data: newRows,
                page: query.page,
                totalCount: newTotalCount,
              };
            });
          }}
          options={tableOptions}
        />
      </Grid>
    </Grid>
  );
};

const ConfirmDenials: React.FC<Props> = (props) => {
  const removals = props.results.data?.interestList.scheduledUpdate.removals;

  return (
    <div>
      <Content {...props} />
      <div className={actionFooterStyles.container}>
        <div className={actionFooterStyles.spacer} />
        {(removals?.results.length ?? 0) === 0 ? (
          <Button disabled size="L" color="primary">Next</Button>
        ) : (
          <Link to="../review">
            <Button size="L" color="primary">Next</Button>
          </Link>
        )}
      </div>
    </div>
  );
};

export default ConfirmDenials;
